import { useEffect, useState } from 'react';
import SEO, { breadcrumbSchema, faqSchema } from '@/components/SEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import CTASection from '@/components/CTASection';
import { faqCategories } from '@/data/faq-data';
import { HelpCircle, Phone, ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const [activeCategory, setActiveCategory] = useState(faqCategories[0].id);
  const [mobileOpen, setMobileOpen] = useState(false);

  const activeItems = faqCategories.find((c) => c.id === activeCategory)?.items ?? [];
  const totalQuestions = faqCategories.reduce((acc, c) => acc + c.items.length, 0);

  return (
    <>
      <SEO
        title="FAQ — Cygnature Production | Questions fréquentes"
        description="Retrouvez les réponses à toutes vos questions sur l'organisation d'événements, les mariages, les artistes et les animations proposés par Cygnature Production."
        path="/faq"
        jsonLd={[
          breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'FAQ', path: '/faq' }]),
          faqSchema(faqCategories.flatMap((c) => c.items.map((it: any) => ({ question: it.question ?? it.q, answer: typeof (it.answer ?? it.a) === 'string' ? (it.answer ?? it.a) : '' }))).filter((q) => q.answer)),
        ]}
      />
      {/* Hero */}
      <section className="px-6 lg:px-8 py-20 md:py-28 bg-petrol text-petrol-foreground">
        <div className="max-w-7xl mx-auto">
          <span className="text-xs font-heading font-medium text-primary uppercase tracking-[0.2em]">Aide & informations</span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold mt-4 leading-tight">Questions fréquentes</h1>
          <p className="mt-5 text-petrol-foreground/60 max-w-2xl text-sm md:text-base leading-relaxed">
            Retrouvez les réponses aux questions les plus posées sur nos services, notre fonctionnement et nos prestations événementielles.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-7 py-3 font-heading font-medium text-sm hover:bg-primary/90 transition-colors">
              Une autre question ? <ArrowRight size={14} />
            </Link>
            <a href="tel:+33671351550" className="inline-flex items-center gap-2 rounded-full px-7 py-3 border border-petrol-foreground/20 text-petrol-foreground font-heading font-medium text-sm hover:border-primary hover:text-primary transition-colors">
              <Phone size={14} /> 06 71 35 15 50
            </a>
          </div>
        </div>
      </section>




      {/* Mobile: sticky category selector */}
      <div className="md:hidden sticky top-[124px] z-20 bg-background px-6 py-3 border-b border-border">
        <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-2">Catégories</p>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full text-left px-4 py-3 rounded-xl font-heading text-sm font-semibold bg-primary/10 text-primary border border-primary/20 flex items-center justify-between"
        >
          <span className="flex items-center gap-2">
            <span className="text-primary">{faqCategories.find(c => c.id === activeCategory)?.icon}</span>
            {faqCategories.find(c => c.id === activeCategory)?.label}
          </span>
          <ChevronDown size={16} className={`text-muted-foreground transition-transform ${mobileOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileOpen && (
          <div className="flex flex-col gap-1 mt-2">
            {faqCategories.filter(c => c.id !== activeCategory).map((cat) => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setMobileOpen(false); }}
                className="text-left px-4 py-3 rounded-xl font-heading text-sm font-semibold text-muted-foreground hover:text-foreground border border-transparent flex items-center gap-2 transition-all"
              >
                <span className="text-muted-foreground/50">{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
        )}
        {!mobileOpen && (
          <button onClick={() => setMobileOpen(true)} className="flex items-center gap-1 mt-2 text-sm font-heading text-muted-foreground">
            Voir plus <ChevronDown size={14} />
          </button>
        )}
      </div>

      {/* FAQ content — sidebar layout desktop, stacked mobile */}
      <section className="px-6 lg:px-8 py-16 md:py-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-12">
            {/* Desktop sidebar */}
            <nav className="hidden md:flex flex-col gap-1.5 w-64 shrink-0 sticky top-36 self-start h-fit">
              <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 px-4">Catégories</p>
              {faqCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`text-left px-4 py-3 rounded-xl font-heading text-xs font-semibold transition-all flex items-center justify-between gap-2 ${
                    activeCategory === cat.id
                      ? 'text-primary bg-primary/5 border border-primary/20 shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-transparent'
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span className={`transition-colors ${activeCategory === cat.id ? 'text-primary' : 'text-muted-foreground/50'}`}>{cat.icon}</span>
                    {cat.label}
                  </span>
                </button>
              ))}
            </nav>

            {/* FAQ items */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center">
                  <span className="text-primary">{faqCategories.find(c => c.id === activeCategory)?.icon}</span>
                </div>
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground">
                    {faqCategories.find(c => c.id === activeCategory)?.label}
                  </h2>
                </div>
              </div>
              <Accordion type="single" collapsible className="space-y-3">
                {activeItems.map((item, idx) => (
                  <AccordionItem key={`${activeCategory}-${idx}`} value={`faq-${idx}`} className="border border-border rounded-xl px-6 bg-background data-[state=open]:border-primary/30">
                    <AccordionTrigger className="font-heading text-sm font-semibold text-foreground hover:no-underline py-5 text-left [text-wrap:pretty]">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-5">{item.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        title="Vous n'avez pas trouvé votre réponse ?"
        subtitle="Contactez-nous directement. Nous répondons sous 48 à 72h à toutes vos questions. Premier échange gratuit et sans engagement."
        buttonLabel="Nous contacter"
      />
    </>
  );
};

export default FAQ;
